import { useCallback, useMemo } from 'react';
import { ApiError, NetworkError } from '@cbhq/data-layer';
import { useUpdateOrganizationInfo } from '@cbhq/identity-api-hooks/account-management/mutation';

import { useOrganization } from '../../contexts/OrganizationContext';
import { logError } from '../../utils/logging';

import { ORGANIZATION_TAB_ID } from './components/EntityTabs';

type UpdateNameParams = {
  entityId: string;
  name: string;
};

function throwIfError(response: unknown) {
  if (response instanceof NetworkError) {
    throw new Error('Network error. Please check your connection.', { cause: response });
  }

  if (response instanceof ApiError) {
    throw new Error(response.message, { cause: response });
  }
}

export function useSettingsMutations(organizationId: string) {
  const { refresh } = useOrganization();
  const updateOrganizationInfo = useUpdateOrganizationInfo();

  const updateName = useCallback(
    async ({ entityId, name }: UpdateNameParams) => {
      if (!organizationId) {
        throw new Error('Organization is required to update the name.');
      }

      try {
        const payload =
          entityId === ORGANIZATION_TAB_ID
            ? {
                organizationId,
                organization: { name },
              }
            : {
                organizationId,
                legalEntityId: entityId,
                legalEntity: { id: entityId, name },
              };

        const response = await updateOrganizationInfo(payload);
        throwIfError(response);
        refresh();
      } catch (error) {
        logError(error, {
          context: 'SettingsMutations.updateName',
          isHandled: true,
          isUserVisible: true,
        });
        throw error;
      }
    },
    [organizationId, refresh, updateOrganizationInfo],
  );

  return useMemo(() => ({ updateName }), [updateName]);
}
